import type { ReactNode } from 'react';
import { Icon, type IconName } from '../ui/Icon';
import { cx, type Tone } from '../ui/primitives/cx';
import type { ProgressMark } from '../../utils/newProduct';

// Rediseño · Bloque E — las piezas de «Nuevo producto» (P-029). Solo dibujan: reciben datos y avisan
// de los toques. Qué se pregunta, qué se valida y qué se manda lo decide `utils/newProduct.ts` y su
// contenedor, `ProductWizard`. Ninguna es un primitive: son de esta pantalla, como las del punto de
// venta (`components/pos/PosParts.tsx`). Todas sus clases empiezan por `n3-product`.

/** El mosaico pastel de un paso: el color y el dibujo de cada pregunta en P-029. */
export function ProductTile({ icon, tone, size = 'md' }: { icon: IconName; tone: Tone | 'neutral'; size?: 'lg' | 'md' | 'sm' }) {
  const px = size === 'lg' ? 24 : size === 'md' ? 20 : 17;
  return (
    <span aria-hidden="true" className={cx('n3-product__tile', `n3-product__tile--${size}`, `n3-product__tile--${tone}`)}>
      <Icon name={icon} size={px} />
    </span>
  );
}

/** El encabezado: «Atrás», el título y en qué paso va, con la barra de cinco segmentos. */
export function ProductHeader({ pill, marks, onBack, backDisabled }: {
  pill?: string;
  marks?: ProgressMark[];
  onBack: () => void;
  backDisabled?: boolean;
}) {
  return (
    <header className="n3-product__head">
      <div className="n3-product__head-row">
        <button className="n3-product__back" disabled={backDisabled} onClick={onBack} type="button">
          <Icon name="chevron-left" size={20} />
          <span>Atrás</span>
        </button>
        <h1 className="n3-product__title" id="product-title">Nuevo producto</h1>
        {pill && <span className="n3-product__pill">{pill}</span>}
      </div>
      {/* Lo que dicen los segmentos ya lo dice «Paso N de 5» con palabras. */}
      {marks && (
        <div aria-hidden="true" className="n3-product__progress">
          {marks.map((mark, index) => <span className={cx('n3-product__bar', `n3-product__bar--${mark}`)} key={index} />)}
        </div>
      )}
    </header>
  );
}

/** Una ayuda bajo la pregunta, con su «i». */
export function ProductHelp({ id, children, fine = false }: { id?: string; children: ReactNode; fine?: boolean }) {
  return (
    <p className={cx('n3-product__help', fine && 'n3-product__help--fine')} id={id}>
      <Icon name="info" size={16} />
      <span>{children}</span>
    </p>
  );
}

/** Lo que falta, junto a lo que hay que cambiar: icono y palabras, no solo rojo. */
export function ProductProblem({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p className="n3-product__problem" id={id} role="alert">
      <Icon name="warning" size={16} />
      <span>{children}</span>
    </p>
  );
}

/**
 * Una opción grande de P-029: un botón de opción (o una casilla) de verdad, escondido, y su tarjeta.
 * Flechas, espacio y lector de pantalla funcionan como en cualquier grupo; lo que se ve es la tarjeta,
 * con palomita cuando está elegida (no solo color).
 */
export function ProductOption({ type, name, value, checked, disabled, onChange, icon, tone, title, hint }: {
  type: 'radio' | 'checkbox';
  name: string;
  value: string;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  icon: IconName;
  tone: Tone | 'neutral';
  title: string;
  hint?: string;
}) {
  return (
    <label className={cx('n3-product__option', checked && 'n3-product__option--on')}>
      <input checked={checked} className="n3-product__option-input" disabled={disabled} name={name} onChange={onChange} type={type} value={value} />
      <span className="n3-product__option-box">
        <ProductTile icon={icon} tone={tone} />
        <span className="n3-product__option-text">
          <span className="n3-product__option-title">{title}</span>
          {hint && <span className="n3-product__option-hint">{hint}</span>}
        </span>
        <span aria-hidden="true" className={cx('n3-product__mark', type === 'checkbox' && 'n3-product__mark--box')}>
          {checked && <Icon name="check" size={14} />}
        </span>
      </span>
    </label>
  );
}

/** «Todas mis tiendas»: un interruptor que es un botón con `aria-pressed`, como en P-029. */
export function ProductSwitch({ on, disabled, onToggle, describedBy, children }: {
  on: boolean;
  disabled?: boolean;
  onToggle: () => void;
  /** La ayuda y, si lo hay, el problema del paso: el interruptor es lo primero que recibe el foco. */
  describedBy?: string;
  children: ReactNode;
}) {
  return (
    <button aria-describedby={describedBy || undefined} aria-pressed={on} className="n3-product__all" disabled={disabled} onClick={onToggle} type="button">
      <span aria-hidden="true" className="n3-product__switch" />
      <span>{children}</span>
    </button>
  );
}

/** Una fila de respuesta: su mosaico, qué es, qué se contestó y «Cambiar». */
export function ProductAnswerRow({ icon, tone, label, value, onChange, changeDisabled, wrap = false, children }: {
  icon: IconName;
  tone: Tone | 'neutral';
  label: string;
  value?: string;
  onChange?: () => void;
  changeDisabled?: boolean;
  /** En la revisión el valor se lee entero; en el resumen de arriba, en un renglón. */
  wrap?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="n3-product__row">
      <ProductTile icon={icon} size="sm" tone={tone} />
      <div className="n3-product__row-text">
        <div className="n3-product__row-key">{label}</div>
        {value !== undefined && <div className={cx('n3-product__row-value', wrap && 'n3-product__row-value--wrap')} title={wrap ? undefined : value}>{value}</div>}
        {children}
      </div>
      {onChange && (
        <button aria-label={`Cambiar ${label.toLowerCase()}`} className="n3-product__change" disabled={changeDisabled} onClick={onChange} type="button">
          Cambiar
        </button>
      )}
    </div>
  );
}
